import {
  BarcodeOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  CreditCardOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Divider, Image, Row, Space, Spin, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import ExternalDocument from '../../components/ExternalDocument';
import AlertHomologMode from '../../components/TestAlert';
import CardDefault from '../../components/shared/CardDefault';
import FooterDefault from '../../components/shared/FooterDefault';
import formatMoney from '../../formatters/money';
import { setupFacebookPixel, setupGoogleTagManager, trackFacebookPixelEvent } from '../../helpers/functions';
import { checkoutService } from '../../services/CheckoutService';
import { trackService } from '../../services/TrackService';
import { copyTextToClipboard } from '../../utils/copyText';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import truncateText from '../../utils/truncateText';
import CardSuccess from './CardSuccess';

const { Text } = Typography;

const getPaymentMethodName = (paymentMethod: string): string => {
  const paymentMethods = {
    boleto: 'Boleto',
    pix: 'Pix',
    credit_card: 'Cartão de Crédito'
  };

  return paymentMethods[paymentMethod as keyof typeof paymentMethods] || paymentMethod;
};

const Success = () => {
  const { transactionId: id } = useParams();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isDocumentSigned, setIsDocumentSigned] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { message } = App.useApp();

  const hasPrevState = !!location.state?.data?.id;
  const hasExternalDocument = !!location.state?.data?.hasExternalDocument;

  let { isLoading, data, error, isError, refetch } = useQuery({
    queryKey: ['result', id],
    queryFn: async () => {
      if (!id) {
        return;
      }

      return await checkoutService.getResultByTransactionId(id);
    },
    enabled: !!id && !hasPrevState && !hasExternalDocument,
  });

  data = hasPrevState ? location.state.data : data;

  if (data && data.error) {
    error = data.error;
    isError = true;
  }

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!data) return;

    if (isError) return;

    const hasPix = !!data.payments?.find((trans: any) => trans.paymentMethod === 'pix' && trans.status == 'waiting_payment');

    if (!hasPix) return;

    if (hasPrevState) {
      navigate(location.pathname, {
        state: { data: undefined },
        replace: true,
      });
    }

    if (data.status == 'paid') return;

    const id = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: ['result', id],
        refetchType: 'all',
      });

      refetch();
    }, 5000);
    return () => {
      clearInterval(id);
    };
  }, [data, queryClient, id, refetch]);

  useEffect(() => {
    if (!data) return;

    if (isError) return;

    if (!data.redirectUrl) return;

    if (data.status != 'paid') return;

    if (data.hasExternalDocument && !isDocumentSigned) return;

    setIsRedirecting(true);
    setTimeout(() => {
      window.location.replace(data.redirectUrl);
    }, 5000);
  }, [data, isDocumentSigned]);

  useEffect(() => {
    if (!data) return;

    if (isError) return;

    // Id estável por transação: este efeito re-roda sempre que `data` muda (polling do PIX a cada
    // 5s, limpeza do location.state), e um uuid novo a cada vez vira um Purchase novo na Meta.
    const eventId = data.id ? `purchase:${data.id}` : v4();

    trackService.trackEvent({
      eventName: 'Purchase',
      eventTime: Date.now(),
      eventSourceUrl: window.location.href,
      checkoutId: data.checkout.id!,
      teamId: data.checkout.teamId!,
      data: {
        deviceInfo: {
          userAgent: navigator.userAgent,
        },
        transaction: {
          transactionId: data.id!,
          status: data.status,
          amount: data.amount,
          currency: 'BRL',
        },
        customer: data.customer!,
        payments: data.payments,
        items: data.items,
      },
    }, eventId);

    const gtmPixel = data?.checkout?.pixels?.find((pixel: any) => pixel.type == 'google-tag-manager');
    const hasGtm = gtmPixel ? gtmPixel.config?.events?.includes('purchase') : false;

    const fbPixel = data?.checkout?.pixels?.find((pixel: any) => pixel.type == 'facebook');
    const hasFacebookPixel = fbPixel ? fbPixel.config?.events?.includes('purchase') : false;

    if (!hasGtm && !hasFacebookPixel) {
      return;
    }

    const arrItems = data.items.map((item: any) => {
      return {
        description: item.description,
        code: item.code,
        amount: item.amount && item.amount > 0 ? item.amount / 100 : 0,
        quantity: item.quantity,
        type: item.type,
      };
    });

    const arrPayments = data.payments.map((payment: any) => {
      return {
        paymentMethod: payment.paymentMethod,
        value: payment.amount && payment.amount > 0 ? payment.amount / 100 : 0,
      };
    });


    if (hasGtm) {
      const validGtm = setupGoogleTagManager(gtmPixel.config.id!);

      if (validGtm) {
        window.dataLayer.push({
          event: 'Purchase',
          eventSourceUrl: window.location.href,
          checkoutId: data.checkout.id!,
          data: {
            amount: data.amount && data.amount > 0 ? data.amount / 100 : 0,
            deviceInfo: {
              userAgent: navigator.userAgent,
            },
            transaction: {
              transactionId: data.id!,
              status: data.status,
              amount: data.amount && data.amount > 0 ? data.amount / 100 : 0,
              currency: 'BRL',
            },
            customer: data.customer!,
            payments: arrPayments,
            items: arrItems,
          },
        });
      }
    }


    if (hasFacebookPixel) {
      const validFacebookPixel = setupFacebookPixel(fbPixel.config.id!);

      if (validFacebookPixel) {
        trackFacebookPixelEvent('Purchase', {
          amount: data.amount && data.amount > 0 ? data.amount / 100 : 0,
          deviceInfo: {
            userAgent: navigator.userAgent,
          },
          transaction: {
            transactionId: data.id!,
            status: data.status,
            amount: data.amount && data.amount > 0 ? data.amount / 100 : 0,
            currency: 'BRL',
          },
          customer: data.customer!,
          payments: arrPayments,
          items: arrItems,
        }, eventId);
      }
    }
  }, [data]);

  // Handle document signed callback
  const handleDocumentSigned = useCallback(() => {
    setIsDocumentSigned(true);
    if (data?.redirectUrl && data?.status === 'paid') {
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.replace(data.redirectUrl);
      }, 5000);
    }
  }, [data?.redirectUrl, data?.status]);

  // Check for redirect when document status changes
  useEffect(() => {
    if (isDocumentSigned && data?.status === 'paid' && data?.redirectUrl) {
      setIsRedirecting(true);
      setTimeout(() => {
        window.location.replace(data.redirectUrl);
      }, 5000);
    }
  }, [isDocumentSigned, data]);

  const handleCompletePayment = () => {
    navigate(`/t/${id}`);
  };

  const renderPartiallyPaidAlert = () => {
    if (data?.status !== 'partially_paid') return null;

    const hasAnyRefusedAndFailed = data?.payments?.some((payment: any) =>
      payment.status === 'refused' || payment.status === 'failed'
    );

    if (!hasAnyRefusedAndFailed) return null;

    const paidAmount = data?.payments?.reduce((total: number, payment: any) => {
      return payment.status === 'paid' ? total + (payment.originalAmount ?? payment.amount) : total;
    }, 0) || 0;

    const amountItems = data?.items?.reduce((total: number, item: any) => {
      if (item.code == 'tax_fee') {
        return total;
      }

      return total + (item.amount || 0) * (item.quantity || 1);
    }, 0) || 0;

    const remainingAmount = Math.round(amountItems - paidAmount);

    return (
      <CardDefault className='bg-orange-50 border-orange-200'>
        <div className="space-y-4">
          <div className='text-lg font-medium text-orange-800 flex items-center'>
            <ExclamationCircleOutlined style={{ fontSize: 20, marginRight: 8, color: '#ea580c' }} />
            Pagamento Parcial
          </div>

          <div className="space-y-2">
            <Text className="text-base text-orange-700">
              Seu pagamento foi processado parcialmente. Para concluir sua compra, é necessário
              efetuar o pagamento do valor restante.
            </Text>

            <div className="bg-orange-100 p-3 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <Text className="text-sm text-orange-700">Valor total:</Text>
                <Text className="text-sm font-medium text-orange-700">
                  {formatMoney((amountItems || 0) / 100.0)}
                </Text>
              </div>
              <div className="flex justify-between items-center mb-2">
                <Text className="text-sm text-orange-700">Valor pago:</Text>
                <Text className="text-sm font-medium text-green-700">
                  {formatMoney((paidAmount) / 100.0)}
                </Text>
              </div>
              <div className="flex justify-between items-center border-t border-orange-200 pt-2">
                <Text className="text-base font-bold text-orange-800">Valor restante:</Text>
                <Text className="text-base font-bold text-orange-800">
                  {formatMoney(remainingAmount / 100.0)}
                </Text>
              </div>
            </div>
          </div>

          <Button
            type="primary"
            size="large"
            block
            onClick={handleCompletePayment}
            className="bg-orange-600 hover:bg-orange-700 border-orange-600"
          >
            Completar Pagamento
          </Button>

          <div className="text-center">
            <Text className="text-sm text-orange-600">
              Clique no botão acima para ser redirecionado à página de pagamento
              e finalizar sua compra.
            </Text>
          </div>
        </div>
      </CardDefault>
    );
  };

  if (isLoading || !data) {
    return <Spin tip="Carregando..." fullscreen size="large"></Spin>;
  }

  if (isError) {
    if (error?.message) {
      return (
        <Alert
          style={{ width: '500px' }}
          message={
            <span>
              <div className="flex items-center justify-center my-4">
                <img width={150} className="" src={resolveAssetUrl(data?.companyLogo)}></img>
              </div>
              <p className="font-bold mb-3">Erro ao carregar o pagamento</p>
              <p className="mb-1">{error?.message}</p>

            </span>
          }
          type="error"
          showIcon
        />
      );
    }

    return <div>Error: {error?.message}</div>;
  }

  const openBoleto = (text: string) => {
    try {
      window.open(text, '_blank');
    } catch (err) {
      message.error('Erro ao abrir o boleto...');
    }
  };

  // Função para formatar a data no formato "dd/mm/yyyy"
  const formatarData = (dataString: string | number | Date) => {
    const data = new Date(dataString);
    const dia = data.getDate().toString().padStart(2, '0');
    const mes = (data.getMonth() + 1).toString().padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  };

  const renderPaymentSummaryHeader = () => (
    <div className="bg-gray-100 h-10 rounded-lg grid grid-cols-12 items-center">
      <div className="col-span-6">
        <Text className="text-base text-gray-500 font-medium ml-2">Tipo Pagamento</Text>
      </div>
      <div className="col-span-3 text-center">
        <Text className="text-base text-gray-500 font-medium">Parcela</Text>
      </div>
      <div className="col-span-3 text-right">
        <Text className="text-base text-gray-500 font-medium mr-2">Valor</Text>
      </div>
    </div>
  );

  const renderPaymentSummaryRow = (trans: any) => (
    <div key={trans.id} className="h-8 grid grid-cols-12 items-center">
      <div className="col-span-6">
        <Text className="text-base text-gray-500 ml-2">
          {getPaymentMethodName(trans.paymentMethod)}
        </Text>
      </div>
      <div className="col-span-3 text-center">
        <Text className="text-base text-gray-500">{trans.installments}</Text>
      </div>
      <div className="col-span-3 text-right">
        <Text className="text-base text-gray-500 mr-2">
          {formatMoney(trans.amount / 100.0)}
        </Text>
      </div>
    </div>
  );

  const renderPaymentSummaryTotal = (totalAmount: number) => (
    <div className="grid grid-cols-12">
      <div className="col-span-9">
        <Text className="text-base text-gray-500 font-bold ml-2">Total</Text>
      </div>
      <div className="col-span-3 text-right">
        <Text className="text-base text-gray-500 mr-2">
          {formatMoney(totalAmount / 100.0)}
        </Text>
      </div>
    </div>
  );

  const renderPixPayment = (trans: any, data: any) => {
    if (trans.status === 'paid') {
      return (
        <CardSuccess
          name={
            <span>
              <QrcodeOutlined style={{ fontSize: 16, marginRight: 5 }} />
              Pix
            </span>
          }
        />
      );
    }

    return (
      <CardDefault className='bg-white'>
        <div className="space-y-4">
          <div className='text-lg font-medium text-black'>
            Pague o Pix para finalizar a compra
          </div>

          <div>
            <Text className="text-base text-gray-500">
              Quando o pagamento for aprovado, você receberá em{' '}
              <span className="font-bold">{data.customer.email}</span> as informações sobre sua(s)
              compra(s). Não se esqueça de conferir sua caixa de spam.
            </Text>
          </div>

          <div className="flex justify-center items-center">
            <Image
              preview={false}
              src={trans.response?.pix?.qrCode}
              width={120}
              height={120}
            />
          </div>

          <div className="bg-white flex justify-center items-center">
            <Text className="text-sm text-gray-500 text-center">
              {truncateText(trans.response?.pix?.qrCodeText, 40)}
            </Text>
          </div>

          <Button
            variant='outlined'
            block
            icon={<CopyOutlined />}
            onClick={() => copyTextToClipboard(trans.response?.pix?.qrCodeText)}
          >
            Copiar código PIX
          </Button>

          <div className="text-gray-500 space-y-1 text-base">
            <div>1. Abra o aplicativo do seu banco e acesse a área Pix</div>
            <div>2. Selecione a opção pagar com código Pix Copia e Cola e cole o código no espaço indicado no aplicativo</div>
            <div>3. Após o pagamento, você receberá por email as informações de acesso à sua compra</div>
          </div>
        </div>
      </CardDefault>
    );
  };

  const handleCopyBoletoCode = async (digitableLine: string) => {
    const success = await copyTextToClipboard(digitableLine);

    if (success) {
      message.success('Código copiado com sucesso!');
    } else {
      message.error('Erro ao copiar código. Tente novamente.');
    }
  };

  const renderBoletoPayment = (trans: any, data: any) => {
    if (trans.status === 'paid') {
      return (
        <CardSuccess
          name={
            <span>
              <BarcodeOutlined style={{ fontSize: 16, marginRight: 5 }} />
              Boleto
            </span>
          }
        />
      );
    }

    return (
      <CardDefault className='bg-white'>
        <div className="space-y-4">
          <div className='text-lg font-medium text-black'>
            <span>
              <BarcodeOutlined style={{ fontSize: 16, marginRight: 5 }} />
              Boleto
            </span>
          </div>

          <div>
            <Text className="text-lg font-medium text-black">
              Pague o boleto até {formatarData(trans.response?.boleto?.expirationDate)} para concluir a compra.
            </Text>
          </div>

          <div>
            <Text className="text-base text-gray-500">
              Quando o pagamento for aprovado, você receberá em{' '}
              <span className="font-bold">{data.customer.email}</span> as informações sobre sua(s)
              compra(s). Não se esqueça de conferir sua caixa de spam.
            </Text>
          </div>

          <div className="bg-white flex justify-center items-center">
            <Text className="text-sm text-gray-500">Código de Barras</Text>
          </div>

          <div className="bg-white flex justify-center items-center">
            <Image
              preview={false}
              src={trans.response?.boleto?.barcode}
              height={50}
            />
          </div>

          <div className="bg-white flex justify-center items-center">
            <Text className="text-xs text-black break-all text-center px-4">
              {trans.response?.boleto?.digitableLine}
            </Text>
          </div>

          <div className="flex bg-white justify-center items-center gap-2">
            <Button
              type="default"
              icon={<BarcodeOutlined />}
              block
              onClick={() => openBoleto(trans.response?.boleto?.url)}
            >
              Visualizar
            </Button>
            <Button
              type="default"
              icon={<CopyOutlined />}
              block
              onClick={() => handleCopyBoletoCode(trans.response?.boleto?.digitableLine)}
            >
              Copiar código
            </Button>
          </div>

          <div className="bg-white flex justify-center items-center">
            <Alert
              className='w-full'
              message={
                <span>
                  O pagamento com boleto pode levar <span className="font-bold">até 3 dias úteis</span> para ser processado.
                </span>
              }
              type="info"
            />
          </div>
        </div>
      </CardDefault>
    );
  };

  const renderCreditCardPayment = (isFree?: boolean, isAwaitingApproval?: boolean) => (
    <CardSuccess
      name={
        <span>
          <CreditCardOutlined style={{ fontSize: 16, marginRight: 8 }} />
          Cartão de crédito
        </span>
      }
      isFree={isFree}
      isAwaitingApproval={isAwaitingApproval}
    />
  );

  const renderPaymentMethod = (trans: any, data: any) => {
    switch (trans.paymentMethod) {
      case 'pix':
        return renderPixPayment(trans, data);
      case 'boleto':
        return renderBoletoPayment(trans, data);
      case 'credit_card': {
        const isFree = !!data.items.some((item: any) => item.code == 'validate-token');

        // Autorizado é reserva, não cobrança: o aviso abaixo do "parabéns" diz que a compra está em análise.
        return renderCreditCardPayment(isFree, trans.status === 'authorized');
      }
      default:
        return null;
    }
  };

  return (
    <div className='p-4'>

      <div className='max-w-2xl m-auto space-y-3'>
        {data?.isTest && <AlertHomologMode />}
        <div className='border-2 border-primary p-3 rounded-lg flex items-center justify-center gap-2'>
          <p className="text-xl text-primary">Compra</p>
        </div>

        {renderPartiallyPaidAlert()}

        {isRedirecting && (
          <Row>
            <Card>
              <Space style={{ width: '100%' }}>
                <Text className="text-gray-500">
                  <ClockCircleOutlined style={{ color: '#15803D', fontSize: 18, marginRight: 5 }} />
                  Você será redirecionado em 5 segundos, caso não seja redirecionado clique no botão ao
                  lado.
                </Text>
                <Button type="default" onClick={() => window.location.replace(data?.redirectUrl)}>
                  CLIQUE AQUI
                </Button>
              </Space>
            </Card>
          </Row>
        )}

        {data?.payments?.filter((trans: any) => ['waiting_payment', 'paid', 'authorized'].includes(trans.status)).map((trans: any) => (
          <div key={trans.id}>
            {renderPaymentMethod(trans, data)}
          </div>
        ))}

        {data?.hasExternalDocument && (
          <ExternalDocument
            key="external-document"
            teamId={data.checkout?.teamId || ''}
            transactionId={id!}
            isSigned={isDocumentSigned}
            onDocumentSigned={handleDocumentSigned}
          />
        )}

        <CardDefault className='bg-white'>
          <div className="space-y-4">
            <div className='text-lg font-medium text-black'>
              <span>
                <DollarOutlined style={{ fontSize: 16, marginRight: 5 }} />
                Resumo da compra
              </span>
            </div>

            <div className="space-y-1">
              {renderPaymentSummaryHeader()}

              {data?.payments?.filter((trans: any) => ['waiting_payment', 'paid', 'authorized'].includes(trans.status)).map((trans: any) =>
                renderPaymentSummaryRow(trans)
              )}
            </div>

            <Divider className='my-1' />

            {renderPaymentSummaryTotal(data?.amount || 0)}
          </div>
        </CardDefault>

        <div className='flex justify-center items-center mt-4'>
          <p className="text-sm text-gray-500">
            Se precisar de ajuda com sua compra, entre em contato com{' '}
            <span className="font-bold">{data?.checkout?.companyEmail}</span>
          </p>
        </div>

        <FooterDefault
          logo={data?.checkout?.companyLogo}
          bgColor='transparent'
          className='mt-4'
        />
      </div>
    </div>
  );
};

export default Success;
